import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Loader2, HardHat, Gauge, Layers3, Info, RefreshCw, ChevronRight, ChevronDown, ArrowLeft, Zap, Sparkles, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─────────────────────────────────────────────────────────────
// Productivity Dashboard — per-contractor monthly trend
//   MODE = 'pf'      → Productivity Factor = qty ÷ manhours  (higher = better)
//   MODE = 'qty'     → Planned vs Actual quantity (no manhour normalization)
// ─────────────────────────────────────────────────────────────

const MODE_EXPLAINER = {
  pf: {
    title: 'Productivity Factor (Output ÷ Manhour)',
    body:
      'Classic construction productivity metric — output per manhour spent. ' +
      'A rising line means the crew is producing more work per hour over time. ' +
      'Plan PF is drawn from planned quantities against planned manhours; ' +
      'Actual PF is your daily-quantities-installed against the manhours booked in Cost Control (Manpower Daily).',
  },
  qty: {
    title: 'Planned vs Actual Quantity',
    body:
      'Straight quantity comparison over time — no manhour normalization. ' +
      'Use this when you want to see raw plan attainment or when the manhour ' +
      'ledger is thin. Bars = quantities per month; the gap between them tells ' +
      'you which months the contractor over- or under-delivered.',
  },
};

export default function FCProductivity({ projectId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState('pf');
  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selected, setSelected] = useState('');
  const [tradeFilter, setTradeFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [fcOptions, setFcOptions] = useState([]);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExplainer, setShowExplainer] = useState(true);
  // Drill-down state
  const [drillContractor, setDrillContractor] = useState(null);
  const [drillActivities, setDrillActivities] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [dailyByKey, setDailyByKey] = useState({});
  const [syncingActuals, setSyncingActuals] = useState(false);
  // Cognara chat state
  const [cognaraOpen, setCognaraOpen] = useState(false);
  const [cognaraSession, setCognaraSession] = useState(null);
  const [cognaraMsgs, setCognaraMsgs] = useState([]); // [{role, content}]
  const [cognaraInput, setCognaraInput] = useState('');
  const [cognaraStreaming, setCognaraStreaming] = useState(false);
  const cognaraEndRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selected)       params.append('contractor', selected);
      if (tradeFilter)    params.append('trade', tradeFilter);
      if (activityFilter) params.append('fc_activity_id', activityFilter);
      const { data } = await api.get(`/v1/projects/${projectId}/productivity?${params}`);
      setRows(data.contractors || []);
      setMonths(data.months || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load productivity');
    } finally {
      setLoading(false);
    }
  }, [projectId, selected, tradeFilter, activityFilter]);

  const fetchFilterOptions = useCallback(async () => {
    if (!projectId) return;
    try {
      // F&C activity dropdown (id + code+name)
      const { data: acts } = await api.get(`/v1/projects/${projectId}/fc/activities?limit=500`);
      setFcOptions((acts.activities || []).map(a => ({
        id: a.id, label: `${a.activityId} · ${a.activityName}`,
      })));
    } catch { /* ignore */ }
    try {
      // Distinct trade codes actually present in ccManpowerDaily
      const { data: trades } = await api.get(`/v1/projects/${projectId}/productivity/trades`);
      setTradeOptions(trades || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const fetchContractors = useCallback(async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/productivity/contractors`);
      setContractors(data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { fetchContractors(); }, [fetchContractors]);
  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Deep-link: ?contractor=Name auto-opens the drill on that vendor
  useEffect(() => {
    const c = searchParams.get('contractor');
    if (c && contractors.some(x => x.contractor === c) && !drillContractor) {
      openContractorDrill(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, contractors]);

  useEffect(() => {
    if (cognaraEndRef.current) cognaraEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [cognaraMsgs]);

  // ── Cognara chat ────────────────────────────────────────────
  const streamCognara = useCallback(async (sessionId, message, { pushUser = true } = {}) => {
    if (pushUser) setCognaraMsgs(prev => [...prev, { role: 'user', content: message }]);
    setCognaraMsgs(prev => [...prev, { role: 'assistant', content: '' }]);
    setCognaraStreaming(true);
    try {
      const url = `${api.defaults.baseURL}/v1/projects/${projectId}/cognara/productivity/chat`;
      const token = localStorage.getItem('ciq_token');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId, message }),
      });
      if (!res.ok || !res.body) throw new Error('stream failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() || '';
        for (const l of lines) {
          if (!l.startsWith('data:')) continue;
          const payload = l.slice(5).trim();
          if (payload === '[DONE]') continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setCognaraMsgs(prev => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: 'assistant', content: (copy[copy.length - 1].content || '') + j.delta };
                return copy;
              });
            } else if (j.error) {
              setCognaraMsgs(prev => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: 'assistant', content: (copy[copy.length - 1].content || '') + `\n\n_[error: ${j.error}]_` };
                return copy;
              });
            }
          } catch { /* keep going */ }
        }
      }
    } catch (e) {
      toast.error(e?.message || 'Cognara stream failed');
    } finally {
      setCognaraStreaming(false);
    }
  }, [projectId]);

  const openCognara = useCallback(async () => {
    setCognaraOpen(true);
    if (cognaraSession) return;
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/cognara/productivity/analyze`,
        drillContractor ? { contractor: drillContractor } : {});
      setCognaraSession(data.sessionId);
      // Auto-seed the first turn so the user immediately sees an analysis.
      streamCognara(data.sessionId, data.seed, { pushUser: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cognara failed to start');
    }
  }, [projectId, drillContractor, cognaraSession, streamCognara]);

  const sendCognara = () => {
    const t = cognaraInput.trim();
    if (!t || !cognaraSession || cognaraStreaming) return;
    setCognaraInput('');
    streamCognara(cognaraSession, t);
  };

  // ── Drill-down loaders ───────────────────────────────────────
  const openContractorDrill = useCallback(async (name) => {
    setDrillContractor(name);
    setDrillLoading(true);
    setExpandedActivity(null);
    setExpandedTrade(null);
    setDailyByKey({});
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/productivity/contractors/${encodeURIComponent(name)}/activities`);
      setDrillActivities(data.activities || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load activities');
    } finally { setDrillLoading(false); }
  }, [projectId]);

  const closeDrill = () => {
    setDrillContractor(null);
    setDrillActivities([]);
    setExpandedActivity(null);
    setExpandedTrade(null);
  };

  const loadDaily = useCallback(async (fcId, trade, contractor) => {
    const key = `${fcId}::${trade}`;
    if (dailyByKey[key]) return;
    try {
      const params = new URLSearchParams({ trade, contractor });
      const { data } = await api.get(`/v1/projects/${projectId}/productivity/activities/${fcId}/daily?${params}`);
      setDailyByKey(prev => ({ ...prev, [key]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load daily rows');
    }
  }, [projectId, dailyByKey]);

  const syncActualsFromProgress = async () => {
    if (!window.confirm('Impute daily actual manhours per contractor / trade from the F&C actualProgress ledger?\n\nUse this when your Cost Control manpower ledger is thin — it back-fills ccManpowerDaily using each day\'s planned rate ± a small productivity variance so the Actual PF chart no longer shows blank.')) return;
    setSyncingActuals(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/productivity/backfill-actual-manhours`);
      toast.success(`Synthesized ${data.inserted} manpower rows (${data.deleted} regenerated)`);
      fetchData();
      if (drillContractor) openContractorDrill(drillContractor);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setSyncingActuals(false); }
  };

  // ── Chart-friendly series ────────────────────────────────────
  const chartSeries = useMemo(() => {
    if (!rows.length) return [];
    // If a specific contractor selected, use its months verbatim.
    // Otherwise aggregate across all contractors (sum qty + MH, then compute PF).
    if (selected && rows.length === 1) return rows[0].months || [];
    const agg = new Map();
    for (const c of rows) {
      for (const m of c.months || []) {
        const cur = agg.get(m.m) || {
          m: m.m, planQty: 0, actQty: 0, planMH: 0, actMH: 0,
        };
        cur.planQty += m.planQty || 0;
        cur.actQty += m.actQty || 0;
        cur.planMH += m.planMH || 0;
        cur.actMH += m.actMH || 0;
        agg.set(m.m, cur);
      }
    }
    return Array.from(agg.values())
      .sort((a, b) => a.m.localeCompare(b.m))
      .map((r) => ({
        ...r,
        planPF: r.planMH > 0 ? +(r.planQty / r.planMH).toFixed(4) : null,
        actPF: r.actMH > 0 ? +(r.actQty / r.actMH).toFixed(4) : null,
      }));
  }, [rows, selected]);

  const summary = useMemo(() => {
    const total = rows.reduce(
      (acc, c) => {
        acc.planQty += c.totals?.planQty || 0;
        acc.actQty += c.totals?.actQty || 0;
        acc.planMH += c.totals?.planMH || 0;
        acc.actMH += c.totals?.actMH || 0;
        return acc;
      },
      { planQty: 0, actQty: 0, planMH: 0, actMH: 0 },
    );
    // PFs are computed as MH-weighted averages ONLY over contractors that
    // report both qty AND MH — mixing buckets from unrelated contractors
    // (e.g. actQty from A but actMH from B) makes the ratio meaningless.
    const validPlan = rows.filter(c => (c.totals?.planQty || 0) > 0 && (c.totals?.planMH || 0) > 0);
    const validAct  = rows.filter(c => (c.totals?.actQty  || 0) > 0 && (c.totals?.actMH  || 0) > 0);
    const wSumPlanQty = validPlan.reduce((s, c) => s + c.totals.planQty, 0);
    const wSumPlanMH  = validPlan.reduce((s, c) => s + c.totals.planMH, 0);
    const wSumActQty  = validAct.reduce((s, c) => s + c.totals.actQty, 0);
    const wSumActMH   = validAct.reduce((s, c) => s + c.totals.actMH, 0);
    total.planPF = wSumPlanMH > 0 ? wSumPlanQty / wSumPlanMH : null;
    total.actPF = wSumActMH > 0 ? wSumActQty / wSumActMH : null;
    total.qtyAttainment = total.planQty > 0 ? (total.actQty / total.planQty) * 100 : null;
    total.pfDelta =
      total.planPF != null && total.actPF != null ? total.actPF - total.planPF : null;
    total.pfCoveragePct = rows.length > 0 ? (validAct.length / rows.length) * 100 : 0;
    return total;
  }, [rows]);

  const knownContractors = useMemo(() => new Set(rows.map(r => r.contractor)), [rows]);
  const filteredContractors = useMemo(
    () => contractors.filter(c => knownContractors.size === 0 || knownContractors.has(c.contractor)),
    [contractors, knownContractors],
  );

  return (
    <div className="space-y-4" data-testid="fc-productivity">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <HardHat className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Contractor Productivity
          </h2>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            · monthly trend by planned vs actual
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="px-2 py-2 rounded-lg text-xs border"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="productivity-contractor-filter"
          >
            <option value="">All contractors ({filteredContractors.length})</option>
            {filteredContractors.map((c) => (
              <option key={c.contractor} value={c.contractor}>
                {c.contractor} · {c.disciplines.join('/')}
              </option>
            ))}
          </select>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="px-2 py-2 rounded-lg text-xs border max-w-[220px]"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="productivity-fc-activity-filter"
            title="Restrict chart + table to one F&C activity"
          >
            <option value="">All F&C activities ({fcOptions.length})</option>
            {fcOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <select
            value={tradeFilter}
            onChange={(e) => setTradeFilter(e.target.value)}
            className="px-2 py-2 rounded-lg text-xs border"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="productivity-trade-filter"
            title="Restrict chart + table to one manpower trade"
          >
            <option value="">All manpower trades ({tradeOptions.length})</option>
            {tradeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {(activityFilter || tradeFilter || selected) && (
            <button
              className="text-[11px] px-2 py-1 rounded font-medium"
              style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              onClick={() => { setActivityFilter(''); setTradeFilter(''); setSelected(''); }}
              data-testid="productivity-clear-filters"
              title="Clear all filters"
            >
              Clear filters
            </button>
          )}
          {/* Mode toggle */}
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="productivity-mode-toggle">
            <button
              onClick={() => setMode('pf')} data-testid="productivity-mode-pf"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: mode === 'pf' ? 'var(--accent-primary)' : 'var(--card-bg)',
                color: mode === 'pf' ? '#ffffff' : 'var(--text-muted)',
              }}
            >
              <Gauge className="w-3.5 h-3.5" /> Output / MH
            </button>
            <button
              onClick={() => setMode('qty')} data-testid="productivity-mode-qty"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: mode === 'qty' ? 'var(--accent-primary)' : 'var(--card-bg)',
                color: mode === 'qty' ? '#ffffff' : 'var(--text-muted)',
                borderLeft: '1px solid var(--border-color)',
              }}
            >
              <Layers3 className="w-3.5 h-3.5" /> Planned vs Actual Qty
            </button>
          </div>
          <Button size="sm" variant="ghost" onClick={fetchData} data-testid="productivity-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={openCognara}
                  data-testid="cognara-open-btn"
                  className="text-violet-500 border-violet-500/40 hover:bg-violet-500/10">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Ask Cognara
          </Button>
          <Button size="sm" variant="outline" onClick={syncActualsFromProgress} disabled={syncingActuals}
                  data-testid="productivity-sync-actuals"
                  className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10">
            {syncingActuals ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
            Sync Actual MH from Progress
          </Button>
        </div>
      </div>

      {/* Explainer */}
      {showExplainer && (
        <div className="rounded-lg p-3 flex gap-3 items-start text-xs"
             style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
             data-testid="productivity-explainer">
          <Info className="w-4 h-4 mt-0.5 flex-none" style={{ color: 'var(--accent-primary)' }} />
          <div className="flex-1">
            <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              {MODE_EXPLAINER[mode].title}
            </div>
            <p style={{ color: 'var(--text-muted)' }}>{MODE_EXPLAINER[mode].body}</p>
          </div>
          <button className="text-xs" onClick={() => setShowExplainer(false)}
                  style={{ color: 'var(--text-disabled)' }}>hide</button>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Plan Qty" value={summary.planQty} decimals={0} testid="kpi-plan-qty" />
        <KpiCard label="Actual Qty" value={summary.actQty} decimals={0}
                 delta={summary.qtyAttainment != null ? `${summary.qtyAttainment.toFixed(1)}% attained` : null}
                 deltaGood={summary.qtyAttainment >= 100} testid="kpi-act-qty" />
        <KpiCard label="Plan PF (Qty/MH)"
                 value={summary.planPF} decimals={3} testid="kpi-plan-pf" />
        <KpiCard label="Actual PF (Qty/MH)"
                 value={summary.actPF} decimals={3}
                 delta={summary.pfDelta != null ? `${summary.pfDelta >= 0 ? '+' : ''}${summary.pfDelta.toFixed(3)} vs plan` : null}
                 deltaGood={summary.pfDelta >= 0} testid="kpi-act-pf" />
      </div>

      {/* Chart */}
      <div className="rounded-lg p-4" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
        ) : chartSeries.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }} data-testid="productivity-empty">
            No productivity data yet. Run <span className="font-semibold" style={{ color: 'var(--accent-primary)' }}>Cascade Contractors from BL</span> on the Activity Register tab, then record planned/actual quantities.
          </div>
        ) : (
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartSeries} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                {mode === 'pf' && (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                )}
                <Tooltip
                  contentStyle={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: 'var(--text-primary)' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {mode === 'qty' && (
                  <>
                    <Bar yAxisId="left" dataKey="planQty" name="Planned Qty" fill="#3b82f6" opacity={0.75} />
                    <Bar yAxisId="left" dataKey="actQty" name="Actual Qty" fill="#10b981" opacity={0.9} />
                  </>
                )}
                {mode === 'pf' && (
                  <>
                    <Bar yAxisId="left" dataKey="planMH" name="Planned MH" fill="#64748b" opacity={0.35} />
                    <Bar yAxisId="left" dataKey="actMH" name="Actual MH" fill="#f59e0b" opacity={0.55} />
                    <Line yAxisId="right" type="monotone" dataKey="planPF" name="Plan PF" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="actPF" name="Actual PF" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-contractor breakdown table */}
      {!selected && rows.length > 0 && !drillContractor && (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <table className="w-full text-xs" data-testid="productivity-breakdown-table">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Contractor</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan Qty</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual Qty</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Attain</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan MH</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual MH</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan PF</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual PF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const t = c.totals || {};
                const attain = t.planQty ? (t.actQty / t.planQty) * 100 : null;
                return (
                  <tr key={c.contractor} className="border-t hover:opacity-80 cursor-pointer"
                      style={{ borderColor: 'var(--border-color)' }}
                      onClick={() => openContractorDrill(c.contractor)}
                      data-testid={`productivity-row-${c.contractor}`}>
                    <td className="p-2 font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                      <ChevronRight className="w-3 h-3" style={{ color: 'var(--accent-primary)' }} />
                      {c.contractor}
                    </td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(t.planQty)}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(t.actQty)}</td>
                    <td className="p-2 text-right font-mono" style={{ color: attain >= 100 ? '#10b981' : attain >= 80 ? '#f59e0b' : '#ef4444' }}>
                      {attain != null ? `${attain.toFixed(1)}%` : '—'}
                    </td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(t.planMH)}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(t.actMH)}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{t.planPF != null ? t.planPF.toFixed(3) : '—'}</td>
                    <td className="p-2 text-right font-mono" style={{ color: t.actPF != null && t.planPF != null ? (t.actPF >= t.planPF ? '#10b981' : '#ef4444') : 'var(--text-muted)' }}>
                      {t.actPF != null ? t.actPF.toFixed(3) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down: contractor → activities → trades → daily */}
      {drillContractor && (
        <ContractorDrillPanel
          contractor={drillContractor}
          activities={drillActivities}
          loading={drillLoading}
          expandedActivity={expandedActivity}
          setExpandedActivity={setExpandedActivity}
          expandedTrade={expandedTrade}
          setExpandedTrade={setExpandedTrade}
          dailyByKey={dailyByKey}
          loadDaily={loadDaily}
          onClose={closeDrill}
        />
      )}

      {/* Cognara chat panel */}
      {cognaraOpen && (
        <CognaraChatPanel
          sessionId={cognaraSession}
          messages={cognaraMsgs}
          streaming={cognaraStreaming}
          input={cognaraInput}
          setInput={setCognaraInput}
          onSend={sendCognara}
          onClose={() => setCognaraOpen(false)}
          endRef={cognaraEndRef}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Cognara Chat side-panel — streams from /cognara/productivity/chat
// ─────────────────────────────────────────────────────────────
function CognaraChatPanel({ sessionId, messages, streaming, input, setInput, onSend, onClose, endRef }) {
  return (
    <div
      className="fixed right-0 top-0 h-full w-full sm:w-[440px] flex flex-col shadow-2xl"
      style={{ background: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)', zIndex: 60 }}
      data-testid="cognara-panel"
    >
      <div className="flex items-center gap-2 p-3 border-b" style={{ borderColor: 'var(--border-color)', background: 'linear-gradient(90deg, #8b5cf615, #3b82f610)' }}>
        <Sparkles className="w-4 h-4" style={{ color: '#8b5cf6' }} />
        <div className="flex-1">
          <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Cognara</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Contractor Productivity Analyst</div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="cognara-close-btn">
          <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs" data-testid="cognara-messages">
        {!sessionId && (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`p-2 rounded-lg ${m.role === 'user' ? 'ml-8' : 'mr-8'}`}
               style={{
                 background: m.role === 'user' ? 'var(--accent-primary)' : 'var(--card-bg-alt)',
                 color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                 border: m.role === 'user' ? 'none' : '1px solid var(--border-color)',
               }}
               data-testid={`cognara-msg-${m.role}-${i}`}>
            {m.role === 'assistant' && (
              <div className="flex items-center gap-1 mb-1 text-[9px] uppercase tracking-wide font-bold" style={{ color: '#8b5cf6' }}>
                <Sparkles className="w-2.5 h-2.5" /> Cognara
              </div>
            )}
            <div className="whitespace-pre-wrap leading-relaxed">{m.content || (streaming && i === messages.length - 1 ? '…' : '')}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg-alt)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder={streaming ? 'Cognara is thinking…' : 'Ask about productivity, red flags, PF trends…'}
          disabled={!sessionId || streaming}
          className="flex-1 px-3 py-2 rounded-lg text-xs border"
          style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          data-testid="cognara-input"
        />
        <button
          onClick={onSend} disabled={!input.trim() || !sessionId || streaming}
          className="px-3 rounded-lg disabled:opacity-40"
          style={{ background: '#8b5cf6', color: '#fff' }}
          data-testid="cognara-send-btn"
        >
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Drill panel: Contractor → Activities (row) → Trades (nested) → Daily (deepest)
// ─────────────────────────────────────────────────────────────
function ContractorDrillPanel({
  contractor, activities, loading,
  expandedActivity, setExpandedActivity,
  expandedTrade, setExpandedTrade,
  dailyByKey, loadDaily, onClose,
}) {
  const totals = useMemo(() => {
    return activities.reduce(
      (acc, a) => {
        acc.planQty += a.planQty || 0;
        acc.actQty += a.actQty || 0;
        acc.planMH += a.planMH || 0;
        acc.actMH += a.actMH || 0;
        return acc;
      },
      { planQty: 0, actQty: 0, planMH: 0, actMH: 0 },
    );
  }, [activities]);

  return (
    <div className="space-y-3" data-testid="productivity-drill-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="drill-back-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All contractors
        </Button>
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--text-primary)' }} className="font-semibold">{contractor}</span>
          <span className="mx-2">·</span>
          {activities.length} F&C activit{activities.length === 1 ? 'y' : 'ies'}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span style={{ color: 'var(--text-muted)' }}>Plan {fmt(totals.planQty)} · Actual {fmt(totals.actQty)}</span>
          <span style={{ color: 'var(--text-muted)' }}>MH {fmt(totals.planMH)} → {fmt(totals.actMH)}</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : activities.length === 0 ? (
        <div className="text-center py-12 text-sm rounded-lg" style={{ color: 'var(--text-muted)', border: '1px solid var(--border-color)' }} data-testid="drill-empty">
          No F&C activities are attributed to {contractor} yet. Run <span className="font-semibold" style={{ color: 'var(--accent-primary)' }}>Cascade Contractors from BL</span> on the Activity Register tab.
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="text-left p-2 font-medium w-8"></th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Activity</th>
                <th className="text-left p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Disc</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Scope</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan Qty</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual Qty</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Attain</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan MH</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual MH</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Plan PF</th>
                <th className="text-right p-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actual PF</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((a) => {
                const isOpen = expandedActivity === a.id;
                const pfGood = a.actPF != null && a.planPF != null && a.actPF >= a.planPF;
                return (
                  <Fragment key={a.id}>
                    <tr className="border-t cursor-pointer hover:opacity-80"
                        style={{ borderColor: 'var(--border-color)' }}
                        onClick={() => setExpandedActivity(isOpen ? null : a.id)}
                        data-testid={`drill-activity-${a.activityId}`}>
                      <td className="p-2">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--accent-primary)' }} /> : <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />}
                      </td>
                      <td className="p-2">
                        <div className="font-mono font-medium" style={{ color: 'var(--accent-primary)' }}>{a.activityId}</div>
                        <div className="truncate max-w-[240px]" style={{ color: 'var(--text-muted)', fontSize: 10 }}>{a.activityName}</div>
                      </td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.disciplineCode}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(a.scopeQuantity)} <span style={{ fontSize: 9 }}>{a.uom}</span></td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(a.planQty)}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(a.actQty)}</td>
                      <td className="p-2 text-right font-mono" style={{ color: a.attain >= 100 ? '#10b981' : a.attain >= 80 ? '#f59e0b' : '#ef4444' }}>
                        {a.attain != null ? `${a.attain.toFixed(1)}%` : '—'}
                      </td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(a.planMH)}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(a.actMH)}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{a.planPF != null ? a.planPF.toFixed(3) : '—'}</td>
                      <td className="p-2 text-right font-mono" style={{ color: pfGood ? '#10b981' : a.actPF != null ? '#ef4444' : 'var(--text-muted)' }}>
                        {a.actPF != null ? a.actPF.toFixed(3) : '—'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={11} className="p-0" style={{ background: 'var(--card-bg-alt)' }}>
                          <TradeSubTable
                            activity={a}
                            contractor={contractor}
                            expandedTrade={expandedTrade}
                            setExpandedTrade={setExpandedTrade}
                            dailyByKey={dailyByKey}
                            loadDaily={loadDaily}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TradeSubTable({ activity, contractor, expandedTrade, setExpandedTrade, dailyByKey, loadDaily }) {
  const trades = activity.trades || [];
  return (
    <div className="p-3">
      <div className="text-[10px] uppercase tracking-wide mb-2" style={{ color: 'var(--text-disabled)' }}>
        Trade breakdown · {trades.length} trade{trades.length === 1 ? '' : 's'}
      </div>
      <div className="rounded-md overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
        <table className="w-full text-[11px]" data-testid={`drill-trades-${activity.activityId}`}>
          <thead>
            <tr style={{ background: 'var(--card-bg)' }}>
              <th className="text-left p-1.5 font-medium w-6"></th>
              <th className="text-left p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Trade / Manpower Type</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Planned MH</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Actual MH</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Δ vs Plan</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const key = `${activity.id}::${t.trade}`;
              const isOpen = expandedTrade === key;
              return (
                <Fragment key={key}>
                  <tr className="border-t cursor-pointer hover:opacity-80"
                      style={{ borderColor: 'var(--border-color)' }}
                      onClick={() => {
                        if (isOpen) {
                          setExpandedTrade(null);
                        } else {
                          setExpandedTrade(key);
                          loadDaily(activity.id, t.trade, contractor);
                        }
                      }}
                      data-testid={`drill-trade-${activity.activityId}-${t.trade}`}>
                    <td className="p-1.5">
                      {isOpen ? <ChevronDown className="w-3 h-3" style={{ color: 'var(--accent-primary)' }} /> : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
                    </td>
                    <td className="p-1.5" style={{ color: 'var(--text-primary)' }}>{t.trade}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmt(t.planMH)}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(t.actMH)}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: t.pfDelta == null ? 'var(--text-muted)' : t.pfDelta <= 0 ? '#10b981' : '#ef4444' }}>
                      {t.pfDelta != null ? `${t.pfDelta >= 0 ? '+' : ''}${t.pfDelta.toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="p-0" style={{ background: 'var(--card-bg-alt)' }}>
                        <DailySubTable data={dailyByKey[key]} uom={activity.uom} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DailySubTable({ data, uom }) {
  if (!data) {
    return <div className="p-4 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  }
  const rows = data.rows || [];
  // Precompute rolling PF trend for the sparkline column
  const pfSeries = rows.map(r => r.actPF).filter(v => v != null);
  const pfMin = pfSeries.length ? Math.min(...pfSeries) : 0;
  const pfMax = pfSeries.length ? Math.max(...pfSeries) : 1;
  const pfRange = pfMax - pfMin || 1;
  const isInt = ['NOS','PCS','EA','EACH','SETS','SET','JOINTS','LOOPS','PACKAGES','PKG','DRAWINGS','DWG','COUNT'].includes((uom||'').toUpperCase());
  const fmtQ = (n) => isInt ? Math.round(n).toString() : n.toFixed(2);
  return (
    <div className="p-3">
      <div className="text-[10px] uppercase tracking-wide mb-2" style={{ color: 'var(--text-disabled)' }}>
        Daily execution · {rows.length} day{rows.length === 1 ? '' : 's'} · UoM {uom}
      </div>
      <div className="max-h-72 overflow-y-auto rounded-md" style={{ border: '1px solid var(--border-color)' }}>
        <table className="w-full text-[11px]" data-testid={`drill-daily-${data.activityId}-${data.trade}`}>
          <thead className="sticky top-0" style={{ background: 'var(--card-bg)' }}>
            <tr>
              <th className="text-left p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Date</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Planned Qty ({uom})</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Actual Qty ({uom})</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Planned MH</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Actual MH</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Plan PF</th>
              <th className="text-right p-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>Act PF</th>
              <th className="text-left p-1.5 font-medium w-28" style={{ color: 'var(--text-muted)' }}>PF Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const good = r.actPF != null && r.planPF != null && r.actPF >= r.planPF;
              return (
                <tr key={r.date} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="p-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{r.date}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmtQ(r.planQty)}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtQ(r.actQty)}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{r.planMH.toFixed(1)}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{r.actMH.toFixed(1)}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{r.planPF != null ? r.planPF.toFixed(3) : '—'}</td>
                  <td className="p-1.5 text-right font-mono" style={{ color: good ? '#10b981' : r.actPF != null ? '#ef4444' : 'var(--text-muted)' }}>
                    {r.actPF != null ? r.actPF.toFixed(3) : '—'}
                  </td>
                  <td className="p-1.5">
                    <PFSparkline rows={rows} idx={idx} min={pfMin} max={pfMax} range={pfRange} />
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center" style={{ color: 'var(--text-muted)' }}>No daily rows.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Inline SVG sparkline of daily Actual PF with a moving pointer for the current row
function PFSparkline({ rows, idx, min, max, range }) {
  const w = 100, h = 20;
  const pts = rows.map((r, i) => {
    const v = r.actPF;
    if (v == null) return null;
    const x = rows.length === 1 ? w / 2 : (i / (rows.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).filter(Boolean);
  const cur = rows[idx];
  const curPt = cur?.actPF != null
    ? { x: rows.length === 1 ? w/2 : (idx / (rows.length - 1)) * w,
        y: h - ((cur.actPF - min) / range) * (h - 4) - 2 }
    : null;
  return (
    <svg width={w} height={h} data-testid="pf-sparkline">
      {pts.length > 1 && (
        <polyline points={pts.join(' ')} fill="none" stroke="var(--accent-primary)" strokeWidth="1.25" opacity="0.7" />
      )}
      {curPt && (
        <circle cx={curPt.x} cy={curPt.y} r="2.5" fill="#10b981" />
      )}
    </svg>
  );
}

function KpiCard({ label, value, decimals = 0, delta, deltaGood, testid }) {
  const shown = value == null ? '—' : (typeof value === 'number' ? value.toFixed(decimals) : String(value));
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-xl font-bold mt-1 font-mono" style={{ color: 'var(--text-primary)' }}>{shown}</div>
      {delta && (
        <div className="text-[10px] mt-1" style={{ color: deltaGood ? '#10b981' : '#ef4444' }}>{delta}</div>
      )}
    </div>
  );
}

function fmt(v) {
  if (v == null) return '—';
  return Math.round(v).toLocaleString();
}
